/**
 * 📅 빠진 납품 일정 만들기 (v2.285.0 · 2026-10-19 · 부장님 「캘린더 없는 8건 지금 올려」) — 시스템 관리 › 연동 도구 · 관리자만
 *
 * 납품예정일이 있는데 네이버웍스 일정이 없는 영업건을 같은 날·같은 엔드유저 묶음으로 찾아 일정을 만든다
 * (영업건을 저장할 때와 같은 방식 — 한 묶음 = 일정 1건 · 메모에 영업번호).
 * 왜 생겼나: 5월 정리 작업이 배포 때마다 예정일을 캘린더 없이 채웠다(v2.283.0 에서 멈춤) — 그때 남은 건들.
 * ⚠만들면 기술지원 포털에 이력 알림이 뜬다 · 엔지니어가 손으로 적어 둔 일정이 있으면 겹칠 수 있다 → [만들기] 전에 확인을 받는다.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import * as adminApi from '../lib/admin-api';
import type { DeliveryMissingGroup } from '../lib/admin-api';

function ymd(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
const keyOf = (g: { date: string; endUser: string }) => `${g.date}|${g.endUser}`;

export function DeliveryMissingCard() {
  const today = new Date();
  const [from, setFrom] = useState(() => ymd(new Date(today.getFullYear(), today.getMonth() - 3, 1)));
  const [to, setTo] = useState(() => ymd(new Date(today.getFullYear(), today.getMonth() + 4, 0)));
  const [found, setFound] = useState<{ from: string; to: string; enabled: boolean; groups: DeliveryMissingGroup[] } | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [done, setDone] = useState<string | null>(null);

  const scanM = useMutation({
    mutationFn: () => adminApi.listDeliveryMissing(from, to),
    onSuccess: (r) => { setFound(r); setPicked(new Set(r.groups.map(keyOf))); },
    onError: (e: Error) => alert(`찾기 실패: ${e.message}`),
  });
  const makeM = useMutation({
    mutationFn: () => adminApi.createDeliveryMissing(found!.from, found!.to,
      found!.groups.filter((g) => picked.has(keyOf(g))).map((g) => ({ date: g.date, endUser: g.endUser }))),
    onSuccess: (r) => {
      const failed = r.results.filter((x) => !x.ok);
      setDone(`웍스에 일정 ${r.created}건을 만들었습니다${failed.length ? ` · 못 만듦 ${failed.length}건(${failed.map((f) => `${f.date} ${f.endUser}`).join(' / ')})` : ''}`);
      scanM.mutate();
    },
    onError: (e: Error) => alert(`만들기 실패: ${e.message}`),
  });
  const toggle = (k: string) => setPicked((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  return (
    <div className="mt-6 rounded border border-slate-200 bg-white p-4 shadow-sm">
      <h2 className="ui-section-title mb-1">📅 빠진 납품 일정 만들기 (네이버웍스)</h2>
      <p className="mb-3 text-xs text-slate-500">
        납품예정일이 있는데 웍스 캘린더 일정이 <b>없는</b> 영업건을 같은 날·같은 엔드유저 묶음으로 찾아 일정을 만듭니다(영업건 저장 때와 같은 방식).
        만들면 기술지원 포털에 이력 알림이 뜹니다 — 손으로 적어 둔 일정이 있으면 겹칠 수 있어요.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="ui-label">납품예정일</label>
        <input type="date" className="ui-input" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="시작 날짜" />
        <span className="text-slate-400">~</span>
        <input type="date" className="ui-input" value={to} onChange={(e) => setTo(e.target.value)} aria-label="끝 날짜" />
        <button type="button" className="btn btn-secondary" onClick={() => { setDone(null); scanM.mutate(); }} disabled={scanM.isPending}>
          {scanM.isPending ? '찾는 중…' : '찾기'}
        </button>
      </div>
      {done && <p className="mt-2 rounded border border-emerald-200 bg-emerald-50 px-2 py-1 text-sm text-emerald-800">{done}</p>}
      {found && (
        <div className="mt-3">
          <p className="text-sm text-slate-600">
            {found.from} ~ {found.to} · 일정이 빠진 묶음 <b>{found.groups.length}</b>개
            {!found.enabled && <span className="ml-1 text-rose-600">(웍스 캘린더 연동이 꺼져 있어 만들 수 없습니다)</span>}
          </p>
          {found.groups.length > 0 && (
            <>
              <ul className="mt-2 space-y-1.5">
                {found.groups.map((g) => (
                  <li key={keyOf(g)} className="rounded border border-sky-200 bg-sky-50/50 px-2 py-1.5 text-sm">
                    <label className="flex items-start gap-2">
                      <input type="checkbox" className="mt-1" checked={picked.has(keyOf(g))} onChange={() => toggle(keyOf(g))} />
                      <span className="min-w-0 flex-1">
                        <span className="font-medium text-slate-800">{g.date}</span> <span className="text-slate-800">{g.endUser || '(엔드유저 없음)'}</span>
                        <span className="ml-1 text-xs text-slate-500">· 영업건 {g.total}건{g.missing < g.total ? ` 중 ${g.missing}건 일정 없음` : ''}</span>
                        <span className="block text-xs text-slate-500">
                          {g.salesIds.map((id, i) => `${id}${g.items[i] ? ` ${g.items[i]}` : ''}`).join(' · ')}
                        </span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={picked.size === 0 || makeM.isPending || !found.enabled}
                  onClick={() => {
                    if (!window.confirm(`고른 ${picked.size}개 묶음의 납품 일정을 네이버웍스 캘린더에 만듭니다.\n기술지원 포털에 이력 알림이 뜹니다.\n\n계속할까요?`)) return;
                    makeM.mutate();
                  }}
                >
                  {makeM.isPending ? '만드는 중…' : `고른 ${picked.size}개 웍스에 만들기`}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
